import React from 'react';
import { Badge, Box, Flex, Text } from '@chakra-ui/react';
import { ArrowRight, CaretDown, LockSimple, Sparkle } from 'phosphor-react';

/* ===== Diagrama: Partida Dobrada (Banco | Contrapartida) ===== */

export function DiagramaPartidaDobrada() {
  const Lado = ({
    titulo,
    cor,
    auto,
    conta,
    sub,
    valor,
  }: {
    titulo: string;
    cor: string;
    auto: boolean;
    conta: string;
    sub: string;
    valor: string;
  }) => (
    <Box
      flex={1}
      minW={0}
      borderWidth="1px"
      borderColor={auto ? 'rgba(15, 23, 42, 0.08)' : 'rgba(99, 102, 241, 0.35)'}
      bg={auto ? 'rgba(255, 255, 255, 0.5)' : 'rgba(99, 102, 241, 0.04)'}
      borderRadius="xl"
      p={3}
    >
      <Flex align="center" justify="space-between" mb={2}>
        <Text fontSize="10px" fontWeight="700" letterSpacing="0.06em" color={cor}>
          {titulo}
        </Text>
        <Flex align="center" gap={1} color={auto ? '#94A3B8' : '#6366F1'}>
          {auto && <LockSimple size={11} weight="bold" />}
          <Text fontSize="10px" fontWeight="600">
            {auto ? 'automático' : 'você escolhe'}
          </Text>
        </Flex>
      </Flex>
      <Text fontSize="sm" fontWeight="600" color="#0F172A">
        {conta}
      </Text>
      <Text fontSize="10px" color="#94A3B8" mb={1}>
        {sub}
      </Text>
      <Text fontSize="sm" fontWeight="700" color={cor}>
        {valor}
      </Text>
    </Box>
  );

  return (
    <Box my={5} maxW="68ch">
      <Flex align="stretch" gap={3} direction={{ base: 'column', md: 'row' }}>
        <Lado
          titulo="DÉBITO"
          cor="#059669"
          auto
          conta="Banco Itaú · c/c 31744"
          sub="conta do banco"
          valor="+ 1.200,00"
        />
        <Flex align="center" justify="center" color="#CBD5E1" flexShrink={0}>
          <Box display={{ base: 'none', md: 'block' }}>
            <ArrowRight size={18} weight="bold" />
          </Box>
          <Box display={{ base: 'block', md: 'none' }}>
            <CaretDown size={18} weight="bold" />
          </Box>
        </Flex>
        <Lado
          titulo="CRÉDITO"
          cor="#DC2626"
          auto={false}
          conta="Receita de serviços"
          sub="contrapartida (POC)"
          valor="1.200,00"
        />
      </Flex>
      <Text fontSize="11px" color="#64748B" mt={2}>
        Entrada de R$ 1.200: o dinheiro entra no banco (débito) e a contrapartida é a receita
        (crédito). Os dois lados somam igual — é a partida dobrada.
      </Text>
    </Box>
  );
}

/* ===== Diagrama: Camadas L1 → L2 → L3 (funil, IA no topo = resíduo) ===== */

interface CamadaDef {
  rotulo: string;
  desc: string;
  pct: number;
  largura: string;
  bg: string;
  cor: string;
  tracejada?: boolean;
}

const CAMADAS: CamadaDef[] = [
  {
    rotulo: 'IA (resíduo)',
    desc: 'só o que sobrou — contraparte nova/ambígua',
    pct: 30,
    largura: '55%',
    bg: 'linear-gradient(135deg, rgba(99,102,241,0.14), rgba(236,72,153,0.14))',
    cor: '#6366F1',
    tracejada: true,
  },
  {
    rotulo: 'L2 · Memória do razão',
    desc: 'aprende do que o contador já lançou + suas correções',
    pct: 28,
    largura: '78%',
    bg: 'rgba(27, 136, 245, 0.10)',
    cor: '#1B88F5',
  },
  {
    rotulo: 'L1 · Regras determinísticas',
    desc: 'tarifa, IOF, impostos, sócio, transferência — padrão fixo',
    pct: 43,
    largura: '100%',
    bg: 'rgba(16, 185, 129, 0.12)',
    cor: '#059669',
  },
];

export function DiagramaCamadas() {
  return (
    <Box my={5} maxW="68ch">
      <Flex direction="column" gap={2} align="center">
        {CAMADAS.map(c => (
          <Box
            key={c.rotulo}
            w={c.largura}
            minW="60%"
            bg={c.bg}
            borderRadius="lg"
            borderWidth={c.tracejada ? '1px' : '0'}
            borderStyle={c.tracejada ? 'dashed' : 'solid'}
            borderColor="rgba(99, 102, 241, 0.4)"
            p={3}
          >
            <Flex align="center" justify="space-between" gap={2}>
              <Flex align="center" gap={2} minW={0}>
                {c.tracejada && <Sparkle size={14} weight="fill" color={c.cor} />}
                <Box minW={0}>
                  <Text fontSize="13px" fontWeight="700" color="#0F172A" isTruncated>
                    {c.rotulo}
                  </Text>
                  <Text fontSize="11px" color="#64748B" noOfLines={1}>
                    {c.desc}
                  </Text>
                </Box>
              </Flex>
              <Text fontSize="16px" fontWeight="700" color={c.cor} flexShrink={0}>
                ~{c.pct}%
              </Text>
            </Flex>
          </Box>
        ))}
      </Flex>
      <Text fontSize="11px" color="#64748B" mt={2} textAlign="center">
        Cada camada só vê o que a de baixo não resolveu. A IA é o último recurso — não a regra.
        (percentuais ilustrativos da bancada de testes)
      </Text>
    </Box>
  );
}

/* ===== Diagrama: Árvore do plano (exemplo estático sintético→analítico) ===== */

interface NoExemplo {
  cl: string;
  nome: string;
  tipo: 'S' | 'A';
  nivel: number;
}

const ARVORE_EXEMPLO: NoExemplo[] = [
  { cl: '1', nome: 'ATIVO', tipo: 'S', nivel: 0 },
  { cl: '11', nome: 'Ativo Circulante', tipo: 'S', nivel: 1 },
  { cl: '111', nome: 'Disponível', tipo: 'S', nivel: 2 },
  { cl: '111020001', nome: 'Banco Itaú C/C', tipo: 'A', nivel: 3 },
  { cl: '111020002', nome: 'Banco Bradesco C/C', tipo: 'A', nivel: 3 },
  { cl: '3', nome: 'RECEITAS', tipo: 'S', nivel: 0 },
  { cl: '31', nome: 'Receita Operacional', tipo: 'S', nivel: 1 },
  { cl: '311010002', nome: 'Receita de serviços', tipo: 'A', nivel: 2 },
];

export function BadgeTipoConta({ tipo }: { tipo: 'S' | 'A' }) {
  const a = tipo === 'A';
  return (
    <Badge
      flexShrink={0}
      borderRadius="full"
      fontSize="9px"
      fontWeight="700"
      px={2}
      bg={a ? 'rgba(16, 185, 129, 0.10)' : 'rgba(99, 102, 241, 0.10)'}
      color={a ? '#059669' : '#6366F1'}
    >
      {a ? 'analítica' : 'sintética'}
    </Badge>
  );
}

export function ArvoreExemplo() {
  return (
    <Box
      my={5}
      maxW="68ch"
      borderWidth="1px"
      borderColor="rgba(15, 23, 42, 0.06)"
      borderRadius="xl"
      overflow="hidden"
      bg="rgba(255, 255, 255, 0.5)"
    >
      {ARVORE_EXEMPLO.map(n => (
        <Flex
          key={n.cl}
          align="center"
          gap={2}
          px={3}
          py={2}
          pl={3 + n.nivel * 5}
          borderBottomWidth="1px"
          borderColor="rgba(15, 23, 42, 0.04)"
        >
          <Text fontFamily="mono" fontSize="11px" color="#94A3B8" minW="72px">
            {n.cl}
          </Text>
          <Text
            fontSize="14px"
            color={n.tipo === 'S' ? '#0F172A' : '#475569'}
            fontWeight={n.tipo === 'S' ? '600' : '500'}
            flex={1}
            isTruncated
          >
            {n.nome}
          </Text>
          <BadgeTipoConta tipo={n.tipo} />
        </Flex>
      ))}
    </Box>
  );
}
